import {
  FileSignature,
  CheckCircle2,
  Clock,
  AlertCircle,
  ExternalLink,
  Send,
  Euro,
} from 'lucide-react';
import {
  useApprenantContrat,
  type JalonContrat,
  type JalonStatus,
} from '../hooks/useApprenantContrat.js';

interface Props {
  apprenantId: string;
}

/**
 * Slice ContratCard — domaine Contractualisation
 *
 * Statique (pas collapsible) — vit dans la colonne gauche.
 * Affiche : convention signée + montant + type financement + jalons.
 *
 * Note : le modèle JalonContrat est conçu pour gérer les 4 workflows
 * (CPF / OPCO / Sous-traitance / Direct) — chacun ayant ses propres jalons
 * et conditions de déblocage.
 */
export function ContratCard({ apprenantId }: Props) {
  const { data: c, isLoading, error } = useApprenantContrat(apprenantId);

  if (isLoading)
    return (
      <Frame title="Contrat & financement">
        <Skel />
      </Frame>
    );
  if (error || !c)
    return (
      <Frame title="Contrat & financement">
        <Err msg={error?.message ?? 'Aucune donnée'} />
      </Frame>
    );

  const conventionOK = !!c.conventionSigneeAt;

  return (
    <Frame
      title="Contrat & financement"
      icon={<FileSignature className="text-primary-500 h-4 w-4" />}
      badge={
        <span
          className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
            conventionOK ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
          }`}
        >
          {conventionOK ? 'Convention signée' : 'Convention en attente'}
        </span>
      }
    >
      {/* Bloc convention */}
      <div className="mb-3 rounded-lg border border-neutral-200 bg-neutral-50 p-3">
        <div className="mb-1 flex items-center justify-between text-[10px] font-semibold uppercase tracking-wider text-neutral-500">
          <span>Convention de formation</span>
          {c.conventionDriveUrl && (
            <a
              href={c.conventionDriveUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-primary-50 text-primary-700 hover:bg-primary-100 flex items-center gap-1 rounded px-1.5 py-0.5"
            >
              Drive <ExternalLink className="h-2.5 w-2.5" />
            </a>
          )}
        </div>
        <div className="flex items-center gap-2 text-xs">
          {conventionOK ? (
            <>
              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
              <span className="text-neutral-700">
                Signée le{' '}
                <span className="font-medium">
                  {new Date(c.conventionSigneeAt!).toLocaleDateString('fr-FR')}
                </span>
              </span>
              {c.conventionGeneratedAt && (
                <span className="text-[10px] text-neutral-400">
                  · générée le {new Date(c.conventionGeneratedAt).toLocaleDateString('fr-FR')}
                </span>
              )}
            </>
          ) : (
            <>
              <Clock className="h-4 w-4 text-amber-500" />
              <span className="text-amber-700">En attente de signature</span>
            </>
          )}
        </div>
      </div>

      {/* Financement */}
      <div className="mb-3 grid grid-cols-2 gap-2">
        <div className="rounded-lg border border-neutral-200 bg-white p-2.5">
          <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-neutral-500">
            <Send className="h-3 w-3" />
            Financeur
          </div>
          <div className="text-primary-700 mt-0.5 text-sm font-semibold">{c.financeurType}</div>
          <div className="text-[10px] text-neutral-500">{c.financeurName}</div>
        </div>
        <div className="border-primary-200 bg-primary-50 rounded-lg border p-2.5">
          <div className="text-primary-700 flex items-center gap-1 text-[9px] uppercase tracking-wider">
            <Euro className="h-3 w-3" />
            Montant total
          </div>
          <div className="text-primary-900 mt-0.5 text-base font-bold">
            {c.montantTotal.toLocaleString('fr-FR')} €
          </div>
          <div className="text-primary-700 text-[10px]">
            {c.jalonsCount.realized}/{c.jalonsCount.total} jalons validés
          </div>
        </div>
      </div>

      {/* Jalons */}
      <div className="mb-3">
        <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-neutral-500">
          Jalons de paiement
        </div>
        <ul className="space-y-1.5">
          {c.jalons.map((j, i) => (
            <JalonRow key={i} jalon={j} />
          ))}
        </ul>
      </div>

      {/* Spécificités */}
      {c.specificites.length > 0 && (
        <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-2.5">
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-neutral-500">
            Spécificités du contrat
          </div>
          <ul className="space-y-0.5 text-[11px] text-neutral-700">
            {c.specificites.map((s, i) => (
              <li key={i}>· {s}</li>
            ))}
          </ul>
        </div>
      )}
    </Frame>
  );
}

// ────────────────────────────────────────────────────────────────────

function JalonRow({ jalon: j }: { jalon: JalonContrat }) {
  return (
    <li className="rounded-md border border-neutral-200 bg-white p-2">
      <div className="flex items-center gap-2">
        <JalonIcon status={j.status} />
        <span className="flex-1 text-xs font-medium text-neutral-800">{j.label}</span>
        <span className="rounded bg-neutral-100 px-1.5 py-0.5 text-[10px] font-semibold text-neutral-700">
          {j.amount.toLocaleString('fr-FR')} €
        </span>
      </div>
      <div className="ml-6 mt-0.5 flex flex-wrap items-center gap-3 text-[10px] text-neutral-500">
        {j.expectedDate && (
          <span>
            Prévu :{' '}
            <span className="font-medium text-neutral-700">
              {new Date(j.expectedDate).toLocaleDateString('fr-FR')}
            </span>
          </span>
        )}
        {j.realizedDate && (
          <span className="text-emerald-600">
            Payé :{' '}
            <span className="font-medium">
              {new Date(j.realizedDate).toLocaleDateString('fr-FR')}
            </span>
          </span>
        )}
        <span className="rounded bg-neutral-100 px-1.5 py-0.5 font-medium">
          {jalonStatusLabel(j.status)}
        </span>
      </div>
      {j.condition && (
        <div className="ml-6 mt-1 text-[10px] italic text-neutral-500">
          Condition : {j.condition}
        </div>
      )}
      {j.actionOFRequired && (
        <div className="ml-6 mt-0.5 flex items-start gap-1 rounded bg-amber-50 px-2 py-1 text-[10px] text-amber-800">
          <AlertCircle className="mt-0.5 h-3 w-3 flex-shrink-0" />
          <span>
            <strong>Action OF :</strong> {j.actionOFRequired}
          </span>
        </div>
      )}
    </li>
  );
}

function JalonIcon({ status }: { status: JalonStatus }) {
  switch (status) {
    case 'payé':
      return <CheckCircle2 className="h-4 w-4 text-emerald-500" />;
    case 'validé':
      return <CheckCircle2 className="text-primary-500 h-4 w-4" />;
    case 'envoyé':
      return <Send className="text-primary-500 h-4 w-4" />;
    case 'en_attente_action':
      return <AlertCircle className="h-4 w-4 text-amber-500" />;
    case 'à_venir':
      return <Clock className="h-4 w-4 text-neutral-400" />;
  }
}

function jalonStatusLabel(s: JalonStatus): string {
  return {
    payé: 'Payé',
    validé: 'Validé',
    envoyé: 'Envoyé',
    en_attente_action: 'Action requise',
    à_venir: 'À venir',
  }[s];
}

function Frame({
  title,
  icon,
  badge,
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  badge?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-neutral-500">
          {icon}
          {title}
        </div>
        {badge}
      </div>
      {children}
    </div>
  );
}

function Skel() {
  return <div className="h-32 animate-pulse rounded bg-neutral-100" />;
}

function Err({ msg }: { msg: string }) {
  return (
    <div className="rounded bg-rose-50 p-3 text-sm text-rose-700">Erreur de chargement : {msg}</div>
  );
}
